/**
 * Helpers for the paginated "list page" shape returned by the kernel list endpoints.
 *
 * Every slice used to reset its list to a shared module-level `list` object on
 * `rejected`. RTK dispatches `rejected` for *cancelled* requests too, so a request that
 * was superseded -- by a tab switch, a filter change, or TablePage's own re-fetch on
 * mount -- blanked data that was already on screen or about to arrive. That is the
 * "loading finished but no rows" symptom.
 *
 * `rejectedList()` keeps what is on screen when the request was merely aborted, and
 * otherwise clears to an empty page.
 *
 * Note on reference stability: the cleared value is a single frozen object rather than a
 * fresh one per call. A number of components fetch on "is this collection empty?" with
 * that collection in their effect dependencies; handing back a new object each time a
 * request fails would flip those dependencies and spin them into a retry loop. Callers
 * that need to know a request finished should observe the request, not the data
 * reference -- see TablePage, which resolves its spinner from the dispatched promise.
 */

const EMPTY_LIST = Object.freeze({
  list: Object.freeze([]),
  pageSize: 25,
  page: 0,
  total: 0,
  nextPage: 0,
});

/** A fresh, mutable empty page -- for slice initial state. */
export const emptyList = () => ({
  list: [],
  pageSize: 25,
  page: 0,
  total: 0,
  nextPage: 0,
});

/**
 * Resolve the new value for a list field in a `rejected` handler.
 *
 * @param {*} current      the field's existing value (an Immer draft is fine)
 * @param {object} action  the rejected action, used to detect cancellation
 */
export const rejectedList = (current, action) => {
  if (action?.meta?.aborted && current) {
    // Superseded request: keep what is on screen, a newer request owns this state.
    return current;
  }
  return EMPTY_LIST;
};

export default rejectedList;
